import { formatNumber } from '@angular/common';
import {
  AEMErrorMessage,
  checkDefined,
  checkErrors,
  getEmptyContent,
  isDisplay,
  isError,
  isValidData,
  Summary
} from '@com-delta-idp/mnx-skymiles-shared';
import { Link } from '@com-delta-idp/mnx-ui-kit';
import { logError } from '@com-delta-idp/mnx-utils';
import { LevelType } from '@com-delta-omnichannel/aura';
import { TYPE } from '../models';
import { Status } from '../models/aem/overview.interface';
import { LoyaltyBalance } from '../models/api/tracker-details.interfaces';
import { StatusTrackerResponse } from '../models/api/tracker.interfaces';
import { getLevel, getTitle } from '../utils/tracker/summary.utils';

export const getStatus = (
  aem: Status | undefined,
  featureFlag: boolean | undefined,
  millionMilerTier: string | undefined,
  tracker: StatusTrackerResponse | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): Summary => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<Summary>(
        'status',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      string,
      [
        StatusTrackerResponse | undefined,
        Status | undefined,
        boolean | undefined
      ]
    >('loyaltyMemberId', tracker, aem, featureFlag)
      ? getStatusObject(
          featureFlag as boolean,
          checkDefined(millionMilerTier),
          tracker as StatusTrackerResponse,
          aem as Status
        )
      : isDisplay<Summary>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'LIFE_TIME_FLOWN');
    return getEmptyContent<Summary>('status', aemErrorMessages, true);
  }
};

const getStatusObject = (
  featureFlag: boolean,
  millionMilerTier: string,
  tracker: StatusTrackerResponse,
  aem: Status
): Summary => {
  if (Object.keys(tracker).length > 0 && featureFlag) {
    const level = millionMilerTier
      ? parseInt(millionMilerTier, 10).toString()
      : '';

    const availMiles = getLifetimeMiles(
      tracker.loyaltyMemberProgramBalance.loyaltyBalances
    );
    const miles = formatNumber(availMiles, TYPE.LOCALE, TYPE.DIGITS_INFO);

    return {
      level: getTrackerStatusLevel(aem, level, availMiles),
      title: aem.statusTitle,
      description: 'Lifetime Miles Flown', //TODO- update with AEM data once available

      value: miles,
      action: getActionDetail(
        aem.learnMoreLabel,
        aem.learnMoreUrl,
        aem.learnMoreId
      ),
      ariaLabels: {
        level: getLevel(aem, getTrackerStatusLevel(aem, level, availMiles)),
        title: getTitle(miles, aem.statusTitle)
      },
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return getEmptyContent<Summary>('', [], true, true);
  }
};

const getTrackerStatusLevel = (
  aem: Status,
  level: string,
  availMiles: number
): LevelType => {
  return availMiles >= Number(aem.minMillerStatusLimit) &&
    availMiles <= Number(aem.maxMillerStatusLimit)
    ? ('0' as LevelType)
    : availMiles < Number(aem.minMillerStatusLimit)
      ? ('' as LevelType)
      : (level as LevelType);
};

const getLifetimeMiles = (balances: LoyaltyBalance[]) => {
  return (
    balances.find((balance) => balance.balanceTypeCode === TYPE.LIFETIME_MQM)
      ?.balanceAmt || 0
  );
};

const getActionDetail = (label: string, url: string, id: string): Link => {
  return { label, url, id };
};
